/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Save, Building2, Layers, Tag, User, Hash, Box, FileEdit, FileText, Info, Activity, Lock, UserCheck
} from 'lucide-react';
import { serviceRequestService } from '../services/serviceRequestService';
import { userService } from '../services/userService';
import StatusBadge from '../components/StatusBadge';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import useServiceRequestTypes from '../hooks/useServiceRequestTypes';
import useServiceRequestTypeHierarchy from '../hooks/useServiceRequestTypeHierarchy';
import HierarchySelector from '../components/hierarchy/HierarchySelector';
import {
  getLeafUid,
  selectionsToValue,
  snapshotOptionsNotIn,
} from '../utils/serviceRequestHierarchy';
import {
  resolveAssetIdentifierLabel,
  resolveEquipmentIdentifierLabel,
  resolveRequestNumberLabel,
  resolveCustomerNameLabel,
  dedupeTypesByCode,
  findLatestTypeByCode,
  DEFAULT_ASSET_IDENTIFIER_LABEL,
  DEFAULT_EQUIPMENT_IDENTIFIER_LABEL,
  DEFAULT_REQUEST_NUMBER_LABEL,
  DEFAULT_CUSTOMER_NAME_LABEL,
} from '../utils/serviceRequestLabels';
import {
  SERVICE_REQUEST_STATUSES,
  SERVICE_REQUEST_STATUS_LABELS,
} from '../constants/serviceRequests';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const SERVICE_REQUESTS_ROUTE = '/service-requests';

const EditServiceRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [originalType, setOriginalType] = useState('');
  const [originalTypeLabels, setOriginalTypeLabels] = useState(null);
  const [originalHierarchySelections, setOriginalHierarchySelections] = useState([]);
  const [form, setForm] = useState({
    requestNumber: '',
    accountName: '',
    subAccountName: '',
    type: '',
    customerName: '',
    assetIdentifier: '',
    equipmentIdentifier: '',
    additionalDetails: '',
    status: 'open',
    subAccountUid: '',
    assignedUserUid: '',
    assignedUserName: '',
    hierarchySelection: {},
  });
  const [assignableUsers, setAssignableUsers] = useState([]);

  const showAccountField = !user?.accountUID;
  const showSubAccountField = !user?.subAccountUID;
  const { types } = useServiceRequestTypes(form.subAccountUid || undefined, { includeInactive: true });
  const activeTypes = dedupeTypesByCode(types.filter(t => t.isActive));
  const currentType = form.type ? findLatestTypeByCode(form.type, types) : null;
  const selectableTypes = currentType && !currentType.isActive
    ? [...activeTypes, currentType]
    : activeTypes;
  const typeUnresolved = form.type === originalType && !currentType && !!originalTypeLabels;
  const assetIdentifierLabel = typeUnresolved
    ? originalTypeLabels.assetIdentifierLabel
    : resolveAssetIdentifierLabel(form.type, types);
  const equipmentIdentifierLabel = typeUnresolved
    ? originalTypeLabels.equipmentIdentifierLabel
    : resolveEquipmentIdentifierLabel(form.type, types);
  const requestNumberLabel = typeUnresolved
    ? originalTypeLabels.requestNumberLabel
    : resolveRequestNumberLabel(form.type, types);
  const customerNameLabel = typeUnresolved
    ? originalTypeLabels.customerNameLabel
    : resolveCustomerNameLabel(form.type, types);
  const selectedTypeUid = form.type
    ? (findLatestTypeByCode(form.type, activeTypes) || currentType)?.uid
    : undefined;
  const { levels: hierarchyLevels, nodes: activeHierarchyNodes, loading: hierarchyLoading } =
    useServiceRequestTypeHierarchy(selectedTypeUid);
  // Keep the saved path selectable on its original type even if some of its
  // options have since been deactivated (the server keeps an untouched path).
  const hierarchyNodes = form.type === originalType
    ? [...activeHierarchyNodes, ...snapshotOptionsNotIn(originalHierarchySelections, activeHierarchyNodes)]
    : activeHierarchyNodes;
  const originalHierarchyLeafUid = originalHierarchySelections.length
    ? originalHierarchySelections[originalHierarchySelections.length - 1].nodeUid
    : null;

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const { data } = await serviceRequestService.getServiceRequestById(id);
        setForm({
          requestNumber: data.requestNumber || '',
          accountName: data.accountName || '',
          subAccountName: data.subAccountName || '',
          type: data.type,
          customerName: data.customerName || '',
          assetIdentifier: data.assetIdentifier || '',
          equipmentIdentifier: data.equipmentIdentifier || '',
          additionalDetails: data.additionalDetails || '',
          status: data.status,
          subAccountUid: data.subAccountUid || '',
          assignedUserUid: data.assignedUserUid || '',
          assignedUserName: data.assignedUserName || '',
          hierarchySelection: selectionsToValue(data.hierarchySelections),
        });
        setOriginalType(data.type);
        setOriginalHierarchySelections(
          [...(data.hierarchySelections || [])].sort((a, b) => a.depth - b.depth),
        );
        setOriginalTypeLabels({
          typeLabel: data.typeLabel || data.type,
          assetIdentifierLabel: data.assetIdentifierLabel || DEFAULT_ASSET_IDENTIFIER_LABEL,
          equipmentIdentifierLabel: data.equipmentIdentifierLabel || DEFAULT_EQUIPMENT_IDENTIFIER_LABEL,
          requestNumberLabel: data.requestNumberLabel || DEFAULT_REQUEST_NUMBER_LABEL,
          customerNameLabel: data.customerNameLabel || DEFAULT_CUSTOMER_NAME_LABEL,
        });
      } catch {
        toastService.error(`Failed to load ${SERVICE_REQUEST_LABEL.singularLower}`);
        navigate(SERVICE_REQUESTS_ROUTE);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, navigate]);

  useEffect(() => {
    if (!form.subAccountUid) { setAssignableUsers([]); return; }
    const fetchAssignableUsers = async () => {
      try {
        const { data } = await userService.getUsers({ subAccountUid: form.subAccountUid, status: 'active', limit: 100 });
        setAssignableUsers(data.users || []);
      } catch {
        toastService.error('Failed to load assignable users');
      }
    };
    fetchAssignableUsers();
  }, [form.subAccountUid]);

  const validateForm = () => {
    const errors = {};
    if (!form.type) { errors.type = 'Type is required'; }
    if (!form.customerName.trim()) { errors.customerName = `${customerNameLabel} is required`; }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (field === 'customerName' && !value.trim()) {
      setFieldErrors(prev => ({ ...prev, customerName: `${customerNameLabel} is required` }));
    } else if (field === 'type' && !value) {
      setFieldErrors(prev => ({ ...prev, type: 'Type is required' }));
    } else {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (hierarchyLoading || !validateForm()) { return; }
    try {
      setSubmitting(true);
      const hierarchyLeafUid = getLeafUid(form.hierarchySelection);
      const payload = {
        ...(form.type !== originalType ? { type: form.type } : {}),
        // Only sent when changed — omitting it keeps the saved path as-is.
        ...(form.type !== originalType || hierarchyLeafUid !== originalHierarchyLeafUid
          ? { hierarchyNodeUid: hierarchyLeafUid }
          : {}),
        customerName: form.customerName.trim(),
        assetIdentifier: form.assetIdentifier.trim() || undefined,
        equipmentIdentifier: form.equipmentIdentifier.trim() || undefined,
        additionalDetails: form.additionalDetails.trim() || null,
        status: form.status,
        assignedUserUid: form.assignedUserUid || null,
      };
      await serviceRequestService.updateServiceRequest(id, payload);
      toastService.success(`${SERVICE_REQUEST_LABEL.singular} updated successfully`);
      navigate(SERVICE_REQUESTS_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      toastService.error(Array.isArray(msg) ? msg[0] : (msg || `Failed to update ${SERVICE_REQUEST_LABEL.singularLower}`));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${fieldErrors[field] ? 'border-error' : 'border-base-300'}`;

  const selectClassFor = (hasError) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:bg-base-200/50 disabled:text-base-content/40 ${hasError ? 'border-error' : 'border-base-300'}`;

  const selectClass = (field) => selectClassFor(!!fieldErrors[field]);

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse p-2 w-full">
        <div className="h-6 w-48 bg-base-300 rounded-lg"></div>
        <div className="glass-card rounded-2xl p-6 h-36 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
        <div className="glass-card rounded-2xl p-6 h-48 bg-base-100/60"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(SERVICE_REQUESTS_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {SERVICE_REQUEST_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1.5 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                {requestNumberLabel}
              </span>
              <StatusBadge status={form.status} />
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              {form.requestNumber || `Update ${SERVICE_REQUEST_LABEL.singular}`}
            </h1>
            <p className="text-xs text-base-content/50">
              Update {SERVICE_REQUEST_LABEL.singularLower} properties, customer details, status, and identifiers.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Scope & Context Card */}
        {(showAccountField || showSubAccountField || form.requestNumber) && (
          <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
              <Building2 size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Scope & Organization
              </h2>
            </div>

            <div className={`grid grid-cols-1 ${showAccountField && showSubAccountField ? 'sm:grid-cols-3' : showSubAccountField ? 'sm:grid-cols-2' : 'sm:grid-cols-3'} gap-4`}>
              {/* Request Number (Read-only) */}
              <div className="space-y-1.5">
                <label htmlFor="requestNumber" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                  <Lock size={11} className="text-base-content/40" />
                  <span>{requestNumberLabel}</span>
                </label>
                <div className="px-4 py-2.5 bg-base-200/60 border border-base-content/10 rounded-xl text-sm font-bold text-base-content/70">
                  {form.requestNumber}
                </div>
              </div>

              {/* Account (Read-only) */}
              {showAccountField && (
                <div className="space-y-1.5">
                  <label htmlFor="accountName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                    <Building2 size={11} className="text-base-content/40" />
                    <span>{ACCOUNT_LABEL.singular}</span>
                  </label>
                  <div className="px-4 py-2.5 bg-base-200/60 border border-base-content/10 rounded-xl text-sm font-bold text-base-content/70 truncate">
                    {form.accountName || '—'}
                  </div>
                </div>
              )}

              {/* Sub Account (Read-only) */}
              {showSubAccountField && (
                <div className="space-y-1.5">
                  <label htmlFor="subAccountName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                    <Layers size={11} className="text-base-content/40" />
                    <span>{SUB_ACCOUNT_LABEL.singular}</span>
                  </label>
                  <div className="px-4 py-2.5 bg-base-200/60 border border-base-content/10 rounded-xl text-sm font-bold text-base-content/70 truncate">
                    {form.subAccountName || '—'}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Request Details — type, its hierarchy, identifiers */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <FileText size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {SERVICE_REQUEST_LABEL.singular} Details
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Type */}
            <div className="space-y-1.5">
              <label htmlFor="type" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Tag size={12} className="text-primary" />
                <span>Type *</span>
              </label>
              <select
                id="type"
                className={selectClass('type')}
                value={form.type}
                disabled={selectableTypes.length === 0}
                onBlur={e => handleBlur('type', e.target.value)}
                onChange={e => {
                  const type = e.target.value;
                  setForm(f => ({
                    ...f,
                    type,
                    hierarchySelection: type === originalType ? selectionsToValue(originalHierarchySelections) : {},
                  }));
                  setFieldErrors(p => ({ ...p, type: null }));
                }}
              >
                {form.type && !selectableTypes.some(t => t.code === form.type) && (
                  <option value={form.type}>
                    {typeUnresolved ? originalTypeLabels.typeLabel : form.type}
                  </option>
                )}
                {selectableTypes.map(t => (
                  <option key={t.uid} value={t.code}>{t.name}{!t.isActive ? ' (Inactive)' : ''}</option>
                ))}
              </select>
              {fieldErrors.type && <p className="text-error text-xs font-medium">{fieldErrors.type}</p>}
              {selectableTypes.length === 0 && (
                <p className="text-base-content/40 text-xs font-medium flex items-center gap-1 mt-1">
                  <Info size={12} />
                  No {SERVICE_REQUEST_LABEL.singularLower} types available — contact an administrator.
                </p>
              )}
            </div>

            {/* Hierarchy — the type's levels (e.g. Category > Asset > Manufacturer), when configured */}
            <HierarchySelector
              inline
              levels={hierarchyLevels}
              nodes={hierarchyNodes}
              value={form.hierarchySelection}
              loading={hierarchyLoading}
              selectClassName={selectClassFor}
              onChange={(hierarchySelection) => setForm(f => ({ ...f, hierarchySelection }))}
            />

            {/* Asset Identifier */}
            <div className="space-y-1.5">
              <label htmlFor="assetIdentifier" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Hash size={12} className="text-primary" />
                <span>{assetIdentifierLabel}</span>
              </label>
              <input
                id="assetIdentifier"
                type="text"
                maxLength={255}
                placeholder={`Enter ${assetIdentifierLabel.toLowerCase()}`}
                className={inputClass('assetIdentifier')}
                value={form.assetIdentifier}
                onChange={e => setForm(f => ({ ...f, assetIdentifier: e.target.value }))}
              />
            </div>

            {/* Equipment Identifier */}
            <div className="space-y-1.5">
              <label htmlFor="equipmentIdentifier" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Box size={12} className="text-primary" />
                <span>{equipmentIdentifierLabel}</span>
              </label>
              <input
                id="equipmentIdentifier"
                type="text"
                maxLength={255}
                placeholder={`Enter ${equipmentIdentifierLabel.toLowerCase()}`}
                className={inputClass('equipmentIdentifier')}
                value={form.equipmentIdentifier}
                onChange={e => setForm(f => ({ ...f, equipmentIdentifier: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Additional Details */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <FileEdit size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Additional Details
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          <div className="space-y-1.5">
            <textarea
              id="additionalDetails"
              rows={4}
              maxLength={4000}
              placeholder={`Enter any additional notes, instructions, or specific context for this ${SERVICE_REQUEST_LABEL.singularLower}...`}
              className="w-full px-4 py-3 bg-base-100 border border-base-300 rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y"
              value={form.additionalDetails}
              onChange={e => setForm(f => ({ ...f, additionalDetails: e.target.value }))}
            />
            <div className="flex justify-end">
              <span className="text-[10px] text-base-content/40 font-medium">
                {form.additionalDetails.length} / 4000
              </span>
            </div>
          </div>
        </div>

        {/* Customer & Assignment */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <UserCheck size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {customerNameLabel} & Assignment
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Status */}
            <div className="space-y-1.5">
              <label htmlFor="status" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <Activity size={12} className="text-primary" />
                <span>Status</span>
              </label>
              <select
                id="status"
                className={selectClass('status')}
                value={form.status}
                onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
              >
                {SERVICE_REQUEST_STATUSES.map(s => (
                  <option key={s} value={s}>{SERVICE_REQUEST_STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>

            {/* Customer Name */}
            <div className="space-y-1.5">
              <label htmlFor="customerName" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <User size={12} className="text-primary" />
                <span>{customerNameLabel} *</span>
              </label>
              <input
                id="customerName"
                type="text"
                maxLength={255}
                placeholder={`Enter ${customerNameLabel.toLowerCase()}`}
                className={inputClass('customerName')}
                value={form.customerName}
                onBlur={e => handleBlur('customerName', e.target.value)}
                onChange={e => { setForm(f => ({ ...f, customerName: e.target.value })); setFieldErrors(p => ({ ...p, customerName: null })); }}
              />
              {fieldErrors.customerName && <p className="text-error text-xs font-medium">{fieldErrors.customerName}</p>}
            </div>

            {/* Assigned To */}
            <div className="space-y-1.5">
              <label htmlFor="assignedUserUid" className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5">
                <UserCheck size={12} className="text-primary" />
                <span>Assign To</span>
              </label>
              <select
                id="assignedUserUid"
                className={selectClass('assignedUserUid')}
                value={form.assignedUserUid}
                disabled={!form.subAccountUid}
                onChange={e => setForm(f => ({ ...f, assignedUserUid: e.target.value }))}
              >
                <option value="">— Unassigned —</option>
                {form.assignedUserUid && !assignableUsers.some(u => u.userId === form.assignedUserUid) && (
                  <option value={form.assignedUserUid}>{form.assignedUserName || form.assignedUserUid}</option>
                )}
                {assignableUsers.map(u => (
                  <option key={u.userId} value={u.userId}>{u.displayName}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2">
          <button
            type="button"
            onClick={() => navigate(SERVICE_REQUESTS_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting || hierarchyLoading}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving Changes...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditServiceRequest;

